<template>
  <div
    class="ImpersonatingNotice w-full relative z-header"
    :class="{
      'bg-bg-success': !userStore.isCustomerSuccessUser,
      'bg-bg-warning': userStore.isCustomerSuccessUser,
    }"
  >
    <div class="layout-container flex items-center justify-center desk:justify-between py-8 desk:py-0 desk:h-40">
      <ConsumerType class="hidden desk:flex" />
      <div class="flex flex-wrap items-center justify-center gap-x-8 desk:gap-x-16 type-sm desk:type-xs">
        <div class="flex flex-row flex-wrap gap-x-4 desk:gap-x-8 items-center justify-center">
          {{ $t('siteNotice.loggedInAs', { name: userProfile.company?.name, id: userProfile.company?.code }) }}
          <div class="flex flex-wrap items-center gap-4">
            <button
              class="btn--text btn--sm"
              @click="userStore.isCustomerSuccessUser ? uiStore.setShowLogInAsCustomer(true) : uiStore.setImpersonateModal(true)"
            >
              {{
                userStore.isCustomerSuccessUser ? $t('siteNotice.changeSalon.customerSuccess') : $t('siteNotice.changeSalon')
              }}
            </button>
            <span class="whitespace-nowrap"> / </span>
            <button
              class="btn--text btn--sm"
              @click="clickStopImpersonating"
            >
              {{
                userStore.isCustomerSuccessUser ? $t('impersonateModal.customerSuccess.stopImpersonating') : $t('mypage.customer.stopShopAsCustomer')
              }}
            </button>
          </div>
        </div>
        <div v-if="orderProposalId" class="flex flex-wrap gap-8 items-center">
          {{ $t('siteNotice.editingOrderProposal') }} #{{ orderProposalId }}
          <div class="flex flex-wrap gap-4">
            <nuxt-link
              :to="$manualUrl('/')"
              class="btn--text btn--sm"
            >
              {{ $t('checkout.orderProposal.addMore') }}
            </nuxt-link>
            <span>/</span>
            <div class="btn--text btn--sm" @click="clearBasket()">
              {{ $t('checkout.orderProposal.cancel') }}
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal
        v-if="showWarning"
        :message="$t('fastorder.changeSalon.warning')"
        :show-abort="true"
        :confirm-text="$t('modal.generic.ok')"
        @close="showWarning = false"
        @click-outside="showWarning = false"
        @confirm="stopImpersonating"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from '~/store/user';
import { useCartStore } from '~/store/cart';
import { storeToRefs } from 'pinia';
import useLinkReplacer from '~/composeables/useLinkReplacer';
import ConfirmModal from '~/components/modals/ConfirmModal.vue';
import LangCurrencySelector from '~/components/menu/LangCurrencySelector.vue';
import ConsumerType from '~/components/body/ConsumerType.vue';
import { useUiStore } from '~/store/ui';

const userStore = useUserStore();
const uiStore = useUiStore();
const cartStore = useCartStore();
const { userProfile } = storeToRefs(userStore);
const { salesUrl, accountUrl } = useLinkReplacer();
const route = useRoute();

const showWarning = ref(false);
const clickStopImpersonating = async() => {
  if (cartStore.cart?.items?.length && route.path?.toString()?.endsWith('fastOrder')) {
    showWarning.value = true;
  } else {
    await stopImpersonating();
  }
};

const stopImpersonating = async() => {
  if (userStore.isCustomerSuccessUser) {
    await userStore.stopImpersonatingCompany();
    navigateTo(accountUrl('selectCustomer'));
  } else {
    await userStore.stopImpersonatingCompany();
    navigateTo(salesUrl('customers'));
  }
};

const clearBasket = async() => {
  await userStore.setCurrentlyEditingOrderProposal(null);
  await cartStore.deleteCart();
  navigateTo(salesUrl('orderProposals'));
};

const orderProposalId = computed(() => {
  return userStore.isEditingOrderProposal ? userStore.userProfile.currentlyEditingOrderProposal?.norceBasketId : null;
});
</script>

<style lang="postcss">
.truncate-p > p {
  @apply truncate text-10 desk:text-12 leading-single;
}
.usp-text {
  opacity: 0;
  transition: opacity 0.5s ease;
}

.usp-text.active {
  opacity: 1;
}
</style>
